import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { TagCategoryWithTags } from '../../types';

interface CategoryPopoverProps {
  category: TagCategoryWithTags;
  selectedTagIds: Set<string>;
  onToggle: (tagId: string) => void;
  /** Map of tagId → number of courses with that tag. Missing/0 = no courses. */
  tagCounts?: Record<string, number>;
}

/**
 * One per category — a borderless trigger button that opens a popover
 * listing the category's tags with checkbox-style multi-select.
 * Closes on click-outside, Esc, or selecting outside the panel.
 */
export const CategoryPopover: React.FC<CategoryPopoverProps> = ({
  category,
  selectedTagIds,
  onToggle,
  tagCounts,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedCount = category.tags.filter((t) => selectedTagIds.has(t.id)).length;
  const isActive = selectedCount > 0;

  // Click-outside + Esc
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`inline-flex items-center gap-1.5 py-1 text-sm transition-colors border-b ${
          isActive
            ? 'text-gray-900 font-semibold border-gray-900'
            : 'text-gray-700 font-medium border-transparent hover:text-gray-900 hover:border-gray-300'
        } ${open ? 'border-gray-900' : ''}`}
      >
        <span>{category.name}</span>
        {isActive && (
          <span className="text-gray-400 font-normal tabular-nums">
            · {selectedCount}
          </span>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            role="listbox"
            aria-multiselectable="true"
            aria-label={`${category.name} tags`}
            className="absolute left-0 top-full mt-2 z-30 min-w-[220px] max-w-[280px] rounded-xl border border-gray-100 bg-white shadow-lg py-1.5 overflow-hidden"
          >
            {category.tags.map((tag) => {
              const isSelected = selectedTagIds.has(tag.id);
              const count = tagCounts?.[tag.id] ?? 0;
              const isEmpty = count === 0 && !isSelected;
              return (
                <button
                  key={tag.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => onToggle(tag.id)}
                  disabled={isEmpty}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                    isEmpty ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'
                  }`}
                >
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 rounded-full border flex-shrink-0"
                    style={{
                      backgroundColor: tag.color || '#9ca3af',
                      borderColor: tag.color || '#9ca3af',
                    }}
                  />
                  <span
                    className={`flex-1 truncate ${
                      isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {tag.name}
                  </span>
                  {count > 0 && (
                    <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                      {count}
                    </span>
                  )}
                  {isSelected && (
                    <Check className="h-3.5 w-3.5 text-gray-900 flex-shrink-0" strokeWidth={2.5} />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
